import gtmSelectItem from './google-tag-manager/select-item';

const serviceSelect = document.querySelector(
  '.js-credit-widget select[name="service_credit_widget_display"]',
) as HTMLSelectElement;
const amountSelect = document.querySelector(
  '.js-credit-widget select[name="amount_display"]',
) as HTMLSelectElement;
const otherAmountInput = document.querySelector(
  '.js-credit-widget__input',
) as HTMLInputElement;
const creditWidgetErrorMsg = document.querySelector(
  '.js-credit-widget .form-error',
);
const purchaseButton = document.querySelector(
  '.js-credit-widget__purchase-btn',
) as HTMLButtonElement;
const stripePriceIdInput = document.querySelector(
  '.js-credit-widget input[name="stripe_price_id"]',
) as HTMLInputElement;
const stripeQuantityInput = document.querySelector(
  '.js-credit-widget input[name="stripe_quantity"]',
) as HTMLInputElement;
const serviceCreditWidgetInput = document.querySelector(
  '.js-credit-widget input[name="service_credit_widget"]',
) as HTMLInputElement;

interface DollarToQuery {
  display_name: string;
  enabled: number;
  name: string;
  price: string;
  queries_remaining: string;
  service_id: number;
}

// Display or hide the other amount input
const showOtherAmountInput = () => {
  const otherAmountDiv = document.querySelector(
    '.js-credit-widget__other-amount',
  ) as HTMLDivElement;

  if (amountSelect.value === 'other') {
    otherAmountDiv.classList.remove('d-none');
    validateOtherAmount(false);
  } else {
    otherAmountDiv.classList.add('d-none');
    creditWidgetErrorMsg?.classList.add('d-none');
    purchaseButton.disabled = false;
    updateStripeFields(); // Update Stripe fields when selecting pre-defined amount
  }
};
amountSelect.addEventListener('input', showOtherAmountInput);

// The amount input is not required, so an empty box passes checkValidity().
// An empty box therefore counts as invalid only when flagEmpty is set, which
// keeps the message off the page until the customer tries to purchase.
const validateOtherAmount = (flagEmpty: boolean): boolean => {
  // A number input holding unparseable text reports an empty value, so ask
  // badInput to tell "nothing typed" apart from "typed something invalid".
  const isEmpty =
    otherAmountInput.value === '' && !otherAmountInput.validity.badInput;
  const isInvalid =
    amountSelect.value === 'other' &&
    (isEmpty ? flagEmpty : !otherAmountInput.checkValidity());

  purchaseButton.disabled = isInvalid;
  creditWidgetErrorMsg?.classList.toggle('d-none', !isInvalid);
  return !isInvalid;
};

otherAmountInput.addEventListener('input', () => validateOtherAmount(false));

// Validate on click, not submit: the add-to-cart tracking listener posts the
// form itself with form.submit(), which a submit listener cannot cancel.
purchaseButton.addEventListener('click', (e) => {
  if (!validateOtherAmount(true)) {
    e.preventDefault();
    otherAmountInput.focus();
  }
});

// Get the Service-Credit Stripe price ID from the form data attribute
const getServiceCreditPriceId = (): string => {
  const form = document.querySelector('.js-credit-widget') as HTMLFormElement;
  return form?.dataset.serviceCreditPriceId || '';
};

// Update service credit widget field when service selection changes
const updateServiceCreditWidget = () => {
  if (serviceSelect && serviceCreditWidgetInput) {
    serviceCreditWidgetInput.value = serviceSelect.value;
  }
};

// Update Stripe form fields when amount changes
const updateStripeFields = () => {
  const rawAmount =
    amountSelect.value === 'other'
      ? otherAmountInput.value
      : amountSelect.value;

  const amount = parseFloat(rawAmount);

  if (!isNaN(amount) && amount > 0) {
    const stripePriceId = getServiceCreditPriceId();
    if (stripePriceIdInput) {
      stripePriceIdInput.value = stripePriceId;
    }
    if (stripeQuantityInput) {
      // For Service-Credit, use the raw amount preserving fractional parts
      // The backend will validate and reject fractional amounts appropriately
      stripeQuantityInput.value = amount.toString();
    }
  } else {
    // Clear fields if no valid amount
    if (stripePriceIdInput) {
      stripePriceIdInput.value = '';
    }
    if (stripeQuantityInput) {
      stripeQuantityInput.value = '';
    }
  }
};

// This allows for quick product switching.
let d2qCache: Record<number, DollarToQuery[]> = {};

const fetchPrices = async (amount: number) => {
  if (Object.hasOwn(d2qCache, amount)) {
    return d2qCache[amount];
  }
  try {
    const response = await fetch(
      `/en/shopping-cart/dollars-to-queries/${amount}`,
    );

    if (!response.ok) {
      throw new Error('There was an error fetching the dollars to queries URL');
    }
    const json = await response.json();
    d2qCache = {
      [amount]: json,
    };
    return json;
  } catch (e) {
    throw new Error(
      'There was either a network issue or invalid json response',
      { cause: e },
    );
  }
};

// Calculate the number of queries per price
const showQueries = async (sendGtmEvent = false) => {
  const creditWidgetQueries = document.querySelector(
    '.js-credit-widget__queries',
  );
  const creditWidgetQueriesMinfraud = document.querySelector(
    '.js-credit-widget__queries--minfraud',
  );
  const scoreQueries = document.querySelector(
    '.js-credit-widget-minfraud-score',
  );
  const insightsQueries = document.querySelector(
    '.js-credit-widget-minfraud-insights',
  );
  const factorsQueries = document.querySelector(
    '.js-credit-widget-minfraud-factors',
  );

  if (creditWidgetQueriesMinfraud) {
    const amount =
      parseInt(amountSelect.value) || parseInt(otherAmountInput.value);

    const selectedService = {
      displayName: 'minFraud',
      name: 'minFraud',
    };

    if (!isNaN(amount)) {
      const json = await fetchPrices(amount);
      if (json) {
        creditWidgetQueriesMinfraud.classList.remove('d-none');
      }

      interface QueriesProps {
        element: Element;
        serviceName: string;
        label: string;
      }

      const queriesAmount = ({ element, label, serviceName }: QueriesProps) => {
        const service = json.find(
          (item: DollarToQuery) => item.name === serviceName,
        );
        element.textContent = `${service.queries_remaining.toLocaleString(
          'en-US',
        )} ${label}`;
      };

      if (scoreQueries) {
        queriesAmount({
          element: scoreQueries,
          label: 'minFraud Score',
          serviceName: 'minfraud-score',
        });
      }

      if (insightsQueries) {
        queriesAmount({
          element: insightsQueries,
          label: 'minFraud Insights',
          serviceName: 'minfraud-insights',
        });
      }

      if (factorsQueries) {
        queriesAmount({
          element: factorsQueries,
          label: 'minFraud Factors',
          serviceName: 'minfraud-factors',
        });
      }

      const service = {
        display_name: 'minFraud',
        enabled: 1,
        name: 'minFraud',
        price: amount.toString(),
        queries_remaining: '1', // Hardcoding since we don't know the exact minFraud service
        service_id: 0,
      };

      //Add unit price to form element for add_to_cart gtm event
      (
        document.querySelector('.js-credit-widget') as HTMLFormElement
      ).dataset.unitPrice = amount.toString();

      if (sendGtmEvent) {
        gtmSelectItem({
          selectedService,
          service,
        });
      }
    }
  }

  if (creditWidgetQueries) {
    const amount =
      parseInt(amountSelect.value) || parseInt(otherAmountInput.value);

    const selectedService = {
      displayName: serviceSelect.options[serviceSelect.selectedIndex].text,
      name: serviceSelect.options[serviceSelect.selectedIndex].dataset.name,
    };

    if (
      (amountSelect.value === 'other' && otherAmountInput.value !== '') ||
      !isNaN(amount)
    ) {
      const json = await fetchPrices(amount);
      const service = json.find((item: DollarToQuery) => {
        return item.name === selectedService.name;
      });

      //Add unit price to form element for add_to_cart gtm event
      (
        document.querySelector('.js-credit-widget') as HTMLFormElement
      ).dataset.unitPrice = service.price.replace('$', '');

      creditWidgetQueries.textContent = `${service.queries_remaining.toLocaleString(
        'en-US',
      )} ${selectedService.displayName}`;

      // If this is the initial load, don't send a select item GTM event
      if (sendGtmEvent) {
        gtmSelectItem({
          selectedService,
          service,
        });
      }
    } else {
      creditWidgetQueries.textContent = '';
    }
  }
};

const debounce = (func: (arg: boolean) => void, arg: boolean) => {
  let timer: number;

  return function () {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      func(arg);
    }, 300);
  };
};

const debouncedShowQueries = debounce(showQueries, true);

serviceSelect?.addEventListener('input', () => {
  showQueries(true);
  updateServiceCreditWidget();
});
serviceSelect?.removeAttribute('disabled');
amountSelect.addEventListener('input', () => {
  showQueries(true);
  updateStripeFields();
});
amountSelect.removeAttribute('disabled');
otherAmountInput.addEventListener('input', () => {
  debouncedShowQueries();
  updateStripeFields();
});
otherAmountInput.removeAttribute('disabled');
showQueries();
updateStripeFields();
updateServiceCreditWidget();
// Enabled last: the templates ship the button disabled so that a page whose
// script never ran cannot post an empty stripe_price_id.
purchaseButton.removeAttribute('disabled');
